import { RadioButtons } from '@storeblocks/radio-buttons';
import { useField } from 'formik';
import { type FC, useState } from 'react';

import { useTexts } from '@/hooks/useTexts';
import { nameof } from '@/util/nameof';

import {
  CitizenshipHighlightedCountries,
  citizenshipHighlightedCountriesOptions,
} from '../types/CitizenshipHighlightedCountries';
import { FormValues } from '../types/FormValues';
import { CitizenshipSelect } from './CitizenshipSelect';

export const CitizenshipRadio: FC = () => {
  const texts = useTexts();

  const [field, meta, helpers] = useField<string>(
    nameof<FormValues>('citizenship'),
  );

  const [isHighlightedCountry, setHighlightedCountry] = useState(
    // Empty value for the initial state should be considered as highlighted
    // country since we don't want the country select to be visible when the
    // form is in a pristine state.
    field.value === '' ||
      Object.values(CitizenshipHighlightedCountries).includes(
        field.value as CitizenshipHighlightedCountries,
      ),
  );

  const citizenshipOptions = citizenshipHighlightedCountriesOptions(texts).map(
    (option) => ({
      ...option,
      checked:
        (isHighlightedCountry && field.value === option.value) ||
        (!isHighlightedCountry &&
          option.value === CitizenshipHighlightedCountries.Other),
    }),
  );

  return (
    <>
      <RadioButtons
        {...field}
        groupName="citizenship-radio"
        data-test="citizenship-radio"
        onChange={(e) => {
          if (e.target.value === CitizenshipHighlightedCountries.Other) {
            setHighlightedCountry(false);
            helpers.setValue('');
          } else {
            setHighlightedCountry(true);
            helpers.setValue(e.target.value);
          }
        }}
        legendProps={{
          title: texts.orders.signatureRight.pep.citizenship.title,
          hint: texts.orders.signatureRight.pep.citizenship.description,
        }}
        options={citizenshipOptions}
        alertMessageType={
          meta.touched && meta.error && isHighlightedCountry
            ? 'error'
            : undefined
        }
        alertMessage={meta.error}
      />

      {/* Allow the user to specify the country them selves from a select list. */}
      {!isHighlightedCountry && <CitizenshipSelect />}
    </>
  );
};
